import React from 'react';
import { Box, IconButton } from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { TBar, ABar, ExpandableSection } from './Widgets';
import { FilterDrawer, Filter, FilterPrefs } from './Filter';
import { formatTime, MAIN_EVENTS, KEY_EVENTS, ETYPES, ETYPES_DESCR, sortKeys, PBS, ISNATIVE,
        Runner, Meeting, RaceStats, getRunner, getRunnersNum, formatShortName, formatDateString, serverRequest } from './Util';
import { RunnerSearch } from './Runner';
import { MultiChart } from './Charts';
import '../Running.css';
import { PBMulti } from './Records';
import { GlobalContext } from '../context/GlobalContextProvider';
import MenuIcon from '@mui/icons-material/Menu';

interface H2HProps {}
interface H2HState {
    h2h: Record<string, number[]>;
    matrix: [];
    meetings: Record<string, Meeting>;
    runners: string[];
    pbs: Record<string, PBS>;
    loading: boolean;
}

export class H2H extends React.Component<H2HProps, H2HState> {
    static contextType = GlobalContext;

    constructor(props: H2HProps) {
        super(props);
        this.state = { h2h: {}, meetings: {}, runners: [], matrix: [], pbs: {}, loading: false }
        this.onAddRunner = this.onAddRunner.bind(this);
        this.onRemoveRunner = this.onRemoveRunner.bind(this);
    }

    componentDidMount() {
        this.loadH2H();
    }

    loadH2H() {
        if (this.state.runners.length < 2)
            return;

        serverRequest("head_to_head?list=" + this.state.runners)
        .then(res => {
            this.setState( {h2h: res.h2h, matrix: res.matrix, meetings: res.meetings} );

            serverRequest("pbs?list=" + this.state.runners)
            .then(res => {
                this.setState( {pbs: res, loading: false} );
            });
        });
    }

    onAddRunner(ID: string) {
        if (ID !== undefined) {
            if (this.state.runners.indexOf(ID) < 0) {
                // making sure it's a new list
                var newlist: string[] = [];
                newlist = newlist.concat(this.state.runners);
                newlist.push(ID);
                this.setState({ runners: newlist, loading: true}, () => {
                    this.loadH2H();
                });
            }
        }
    }

    onRemoveRunner(e: React.MouseEvent<HTMLElement>) {
        const rID = e.currentTarget.dataset.runnerKey;
        if (!rID) return;
        var ind = this.state.runners.indexOf(rID);
        if (ind >= 0) {
            // making sure it's a new list
            var newlist: string[] = [];
            newlist = newlist.concat(this.state.runners);
            newlist.splice(ind, 1)
            this.setState({ runners: newlist, loading: true}, () => {
                this.loadH2H();
            });
        }
    }

    openMenu = () => {
        const ctx = this.context as React.ContextType<typeof GlobalContext>;
        ctx.setMenuOpen(!ctx.menuOpen);
    }

    render() {
        var h2h = this.state.h2h;
        var meetdict = this.state.meetings;
        var racekeys = sortKeys(meetdict, [{key: 'Date', asc: false}]);
        var matrix = this.state.matrix;
        var runners = this.state.runners;
        var nrunners: number = runners.length;

        var appbar = <ABar position="top" >
                <TBar>
            <IconButton sx={{ color: 'var(--paper-color)'}} onClick={this.openMenu}>
          <MenuIcon fontSize="large"></MenuIcon>
                  </IconButton>
                  <div className='largetext'>Head to Head</div>
                  <Box sx={{ flexGrow: 1}} />
                  <RunnerSearch onSelectRunner={this.onAddRunner} />
                </TBar>
                </ABar>

        var meeting_table = <></>
        var message = <></>
        var matrix_table = <></>
        var chart = <></>
        var pbtable = <></>

        var rows: JSX.Element[] = [];
        var cols: JSX.Element[] = [];

        if (nrunners > 0) {
            // Runners matrix
            cols.push(<td key="Corner"></td>)
            for (var r1=0; r1<nrunners; r1++) {
                var runner1: Runner = getRunner(runners[r1]);
                cols.push(<td className='vertical' key={r1}>{formatShortName(runner1)}</td>)
            }
            cols.push(<td className='vertical' key={'Totals'}><b>Totals</b></td>)
            var head = (<tr>{cols}</tr>);

            for (r1=0; r1<nrunners; r1++) {
                runner1 = getRunner(runners[r1]);
                cols = [];
                cols.push(<td style={{textAlign: 'left'}} key="Name" >
                        <IconButton onClick={this.onRemoveRunner} data-runner-key={runner1.ID}>
                            <HighlightOffIcon />
                        </IconButton>
                        {formatShortName(runner1)}
                    </td>)
                var won = 0;
                var lost = 0;
                for (var r2=0; r2<nrunners; r2++) {
                    if (!this.state.loading && r1 !== r2) {
                        won += matrix[r1][r2];
                        lost += matrix[r2][r1];
                        cols.push(<td key={r2}>{matrix[r1][r2] + "-" + matrix[r2][r1]}</td>)
                    } else
                        cols.push(<td key={r2}>--</td>)
                }
                cols.push(<td key="Totals"><b>{won + "-" + lost}</b></td>)
                rows.push(<tr key={r1}>{cols}</tr>)
            }
            matrix_table = <div className='card'>
                                <table><thead>{head}</thead><tbody>{rows}</tbody></table>
                            </div>

            chart = <ExpandableSection title="Chart" details="">
                            <MultiChart runners={this.state.runners} />
                        </ExpandableSection>

        }

        if (nrunners > 1 && !this.state.loading) {
            rows = [];
            cols = [];
            cols.push(<th>Date</th>);
            cols.push(<th>Meeting</th>);
            cols.push(<th>Event</th>)
            for (var n=0; n<nrunners; n++) {
                var runner = getRunner(runners[n]);
                if (!runner)
                    continue; 
                cols.push(<th>{formatShortName(runner)}</th>);
            }
            rows.push(<tr>{cols}</tr>);

            var meeting_rows = racekeys.map((key, ind) => {
                var meeting: Meeting = meetdict[key];
                var rec = h2h[meeting.MeetingID + "-" + meeting.Event];  // unique key for H2H is meetingID-Event
                if (rec === undefined)
                    return <tr></tr>;

                cols = [];
                cols.push(<td key="Date">{formatDateString(meeting.Date)}</td>);
                cols.push(<td className='meetingcol' key="Meeting">{meeting.Meeting}<br/>{meeting.Venue}</td>);
                cols.push(<td key="Event">{meeting.Event}</td>);

                // Finds winner
                var bestrunner = -1;
                var besttime = 999999;
                for (var n=0; n<nrunners; n++) {
                    if (rec[n+nrunners] > 0) {
                        if (rec[n+nrunners] < besttime) {
                            besttime = rec[n+nrunners];
                            bestrunner = n;
                        }
                    }
                }

                for (n=0; n<nrunners; n++) {
                    if (rec[n+nrunners] > 0) {
                        var style = (n === bestrunner) ? {fontWeight: 'bold'} : {};
                        cols.push(<td key={n} style={style}>{formatTime(rec[n+nrunners], meeting.Event)}</td>);
                    } else
                        cols.push(<td key={n}>--</td>);
                }
                return(<tr key={ind}>{cols}</tr>);
            });
            meeting_table = <ExpandableSection title="Results" details={racekeys.length + " races"} >
                        <table><tbody>{rows}{meeting_rows}</tbody></table>
                    </ExpandableSection>

            pbtable = <ExpandableSection title="Personal Bests" details="">
                    <PBMulti pblist={this.state.pbs} runners={this.state.runners} />
                    </ExpandableSection>
        } else {
            message = <h2 style={{padding: '16px'}}>Select two or more runners</h2>
        }

        return (<div>
            {appbar}
            {message}
           <div style={{margin: '16px'}}> {matrix_table} </div>
          <div style={{ paddingTop: '16px', paddingLeft: '16px', paddingRight: '16px' }}>{chart}</div>
          <div style={{ paddingTop: '16px', paddingLeft: '16px', paddingRight: '16px' }}>  {pbtable}</div>
          <div style={{ paddingTop: '16px', paddingLeft: '16px', paddingRight: '16px' }}> {meeting_table} </div>
        </div>)
}
}

interface StatsProps {}
interface StatsState {
    stats: RaceStats;
    mainOnly: boolean;
}

export class Stats extends React.Component<StatsProps, StatsState> {

  static contextType = GlobalContext;
    constructor(props: StatsProps) {
        super(props);
        this.state = { stats: {} as RaceStats, mainOnly: true }
        this.loadStats = this.loadStats.bind(this);
    }

    componentDidMount() {
        this.loadStats(new Filter(0, 0, '', '', '', true, ''), true);
    }

    loadStats(filter: Filter, requery: boolean) {
        if (requery) {
            serverRequest("racestats?age_group=" + filter.age_group + "&gender=" + filter.gender)
            .then(res => {
                this.setState( {stats: res, mainOnly: filter.mainOnly } );
            });
        } else {
            this.setState( {mainOnly: filter.mainOnly } );
        }
    }

    static filter_prefs: FilterPrefs = {year: false, month: false,
        event: false, all_event: true, mainOnly: true,
        gender: true, all_gender: true, age_group: true, all_age_group: true, meeting: false };

    openMenu = () => {
        const ctx = this.context as React.ContextType<typeof GlobalContext>;
        ctx.setMenuOpen(!ctx.menuOpen);
    }

    render() {
        var stats = this.state.stats;
        if (!stats.actives || stats.actives.length < 1)
            return <div></div>

        var filter = <FilterDrawer onChange={this.loadStats} prefs={Stats.filter_prefs} start_filter={new Filter()} display_filter={true} />
                
        var maxetypes = ETYPES.length;
        var event_list = ETYPES.concat((this.state.mainOnly ? KEY_EVENTS : MAIN_EVENTS).reverse());
        var pr_ind = event_list.indexOf("parkrun");  // Removes redundant event 
        if (pr_ind >= 0)
            event_list.splice(pr_ind, 1)

        pr_ind = event_list.indexOf("notparkrun");  // Removes redundant event type
        if (pr_ind >= 0) {
            event_list.splice(pr_ind, 1)
            // console.log("Removed notparkrun")
            maxetypes -= 1;
        }

        // console.log(event_list);

        // actives[n] = [year, runners, results] in increasing order from a starting year
        var actives: number[][] = stats.actives;
        var num_years = actives.length;
        var end_year = actives[num_years-1][0]
        var non_parkrun: number[][] = stats.non_parkrun;

        // Prepares matrix of finishers by events and year (rows = events, cols = years)
        var fin_matrix = new Array(event_list.length).fill(0).map(() => new Array(num_years).fill(0));

        // evtypes[n] = [year, event, results]
        var evtypes = stats.types;
        // console.log(evtypes);

        for (var n=0; n<evtypes.length; n++) {
            var col = end_year - evtypes[n][0];
            if (col < 0 || col >= num_years)
                continue; 

            var row = event_list.indexOf(evtypes[n][1]);
            if (row >= 0) {
                fin_matrix[row][col] = evtypes[n][2];
            }
        }

        // We travel years and events in reverse to show the latest and the longest at the top
        var rev_actives = actives.slice().reverse();
        var years_row = <tr><th></th>{rev_actives.map((act, index) => { return (<th key={index}>{act[0]}</th>) })}</tr>
        var runners_row = <tr><th className='sticky-col first-col'>Runners</th>{rev_actives.map((act, index) => { return (<td key={index}>{act[1]}</td>) })}</tr>
        var results_row = <tr><th className='sticky-col first-col'>Results</th>{rev_actives.map((act, index) => { return (<td key={index}>{act[2]}</td>) })}</tr>

        var rev_nonparkrun = non_parkrun.slice().reverse();
        var np_runners_row = <tr><th className='sticky-col first-col'>Non Parkrun Runners</th>{rev_nonparkrun.map((act, index) => { return (<td key={index}>{act[1]}</td>) })}</tr>
        var np_results_row = <tr><th className='sticky-col first-col'>Non Parkrun Results</th>{rev_nonparkrun.map((act, index) => { return (<td key={index}>{act[2]}</td>) })}</tr>

        var rows = fin_matrix.map((row, row_ind) => {
            var cols = row.map((rec, col_ind) => {return <td>{rec > 0 ? rec : '--'}</td>});
            var row_label = (row_ind < maxetypes) ? ETYPES_DESCR[row_ind] : event_list[row_ind];
            var style = (row_ind < maxetypes) ? {'backgroundColor' : 'var(--secondary-color)'} : {};
            // console.log("Row " + row_ind + " " + ETYPES_DESCR[row_ind] + " " + event_list[row_ind] + " " + maxetypes);
            return(<tr key={row_ind}>
                        <th className='sticky-col first-col' style={style}>{row_label}</th>
                        {cols}
                    </tr>);
        });

        var empty_row = <tr><th className='sticky-col first-col'></th><td colSpan={num_years}></td></tr>
        var type_table = <table className='bicolortable'><thead>{years_row}</thead><tbody>{runners_row}{results_row}{np_runners_row}{np_results_row}{empty_row}{rows}</tbody></table>
        var mainstats = <p><b>{getRunnersNum()}</b> Runners | <b>{stats.meetings}</b> Meetings | <b>{stats.finishers}</b> Finishers</p>

        const etypes = ['#', 'Name', 'Total', 'Parkrun', 'Road', 'Track', 'XC', 'Others']
        const etypes_cols = etypes.map((et, index) => {return <th>{et}</th>});

        var runners_stats = stats.runners;
        var runners_rows = runners_stats.map((rs, index) => { 
            const run = getRunner(rs['ID']);
            const tot = rs['Total'];
            const pr = rs['PR'];
            const road = rs['R'];
            const track = rs['T'];
            const xc = rs['XC'];
            const others = tot - pr - road - track - xc;
            return <tr key={index}><td>{index+1}</td>
                                <td><b>{run.First + " " + run.Last}</b></td>
                                <td><b>{tot}</b></td>
                                <td>{pr}</td>
                                <td>{road}</td>
                                <td>{track}</td>
                                <td>{xc}</td>
                                <td>{others}</td>
                            </tr>
        })

        const runners_table = <table className='bicolortable'><thead><tr>{etypes_cols}</tr></thead><tbody>{runners_rows}</tbody></table>

        var appbar = <ABar position="top"  style={{top: ISNATIVE ? '35px' : '0'}}>
                        <TBar>
                            <IconButton sx={{ color: "var(--paper-color)" }} onClick={this.openMenu}>
                                <MenuIcon fontSize="large"></MenuIcon>
                            </IconButton>
                            <div className='largetext title-on-dark'>Club Stats</div>
                            <Box sx={{ flexGrow: 1}} />
                            {filter}
                        </TBar>
                     </ABar>

        return <div>
            {appbar}
            <div style={{ padding: '16px 16px 16px 16px' }}>
            {mainstats}
            <h2>Number of Results per Year and Event</h2>
            <div className="stats-table-wrap">{type_table}</div>
            <h2 style={{marginTop: '20px'}}>Number of Official Results per Runner</h2>
            <div className="stats-table-wrap">{runners_table}</div>
            </div>
        </div>
    }
}
